/**
 * RegionAccordion — the "Regions & Bonuses" list used by the map explorer
 * (`app/(app)/maps/[id].tsx`) and the in-game map details
 * (`app/(app)/games/[id]/map-details.tsx`).
 *
 * Each region is a collapsible row. Tapping the header expands it in place to
 * show the region bonus and its territories — no detail cards float above the
 * list. Tapping a territory (here or on the map) highlights it inside its
 * region's expanded body and surfaces that territory's adjacencies.
 *
 * Pure presentational: selection/expansion state lives in the parent screen so
 * it can be kept in sync with the MapView (regionFocusId / selectedTerritoryId).
 */

import React from 'react'
import { View, Text, TouchableOpacity, type ScrollView } from 'react-native'
import { centerScrollOffset } from '../../lib/centerScroll'

interface AccordionTerritory {
  id: string
  name: string
  adjacencies: string[]
  /** One-way borders: usable outbound only; the far side has no edge back. */
  oneWayBorders?: string[]
  /**
   * Per-border die size for THIS territory's rolls. A bare number covers both
   * modes; the object form splits them. Absent = the default d6.
   */
  borderDice?: Record<string, number | { attack?: number; defend?: number }>
  regionId?: string
}

/**
 * Territories in no region still need a home in this list — Capture the Flag's
 * arena is 29 region-less cells, including the High Tower, which is the map's
 * single biggest one-way attacker. Without this group they'd be invisible here
 * and tapping one on the map would expand nothing.
 */
export const NO_REGION_ID = '__no_region__'

interface AccordionRegion {
  id: string
  name: string
  bonus: number
  territoryIds: string[]
}

export interface RegionAccordionProps {
  regions: AccordionRegion[]
  territories: AccordionTerritory[]
  regionColors: Record<string, string>
  /** The region whose body is expanded, or null when all are collapsed. */
  expandedRegionId: string | null
  /** The highlighted territory (also highlighted on the map), or null. */
  selectedTerritoryId: string | null
  /** Toggle a region's expanded state. */
  onToggleRegion: (regionId: string) => void
  /** Select/deselect a territory from within its region body. */
  onSelectTerritory: (territoryId: string) => void
  /**
   * The ScrollView this accordion lives in. When provided, selecting a
   * territory (typically by tapping it on the map) scrolls that territory's row
   * to the vertical centre of the scroll view. Omit to keep the list static.
   */
  scrollParentRef?: React.RefObject<ScrollView | null>
  /** Current visible height of `scrollParentRef` (for centring math). */
  getViewportHeight?: () => number
}

export function RegionAccordion({
  regions,
  territories,
  regionColors,
  expandedRegionId,
  selectedTerritoryId,
  onToggleRegion,
  onSelectTerritory,
  scrollParentRef,
  getViewportHeight,
}: RegionAccordionProps) {
  const territoryById = React.useMemo(() => {
    const out: Record<string, AccordionTerritory> = {}
    for (const t of territories) out[t.id] = t
    return out
  }, [territories])

  // Reverse index for one-way edges. Being SHELLED by a tower you cannot answer
  // is exactly the kind of thing a player needs to see on a territory, and it's
  // unreadable from the territory's own record — it lives on the attacker.
  const attackedBy = React.useMemo(() => {
    const out: Record<string, string[]> = {}
    for (const t of territories) {
      for (const target of t.oneWayBorders ?? []) (out[target] ??= []).push(t.id)
    }
    return out
  }, [territories])

  // Append a synthetic group for territories no region lists, so they are
  // reachable in this list at all.
  const groups = React.useMemo(() => {
    const covered = new Set(regions.flatMap((r) => r.territoryIds))
    const loose = territories.filter((t) => !covered.has(t.id)).map((t) => t.id)
    return loose.length
      ? [...regions, { id: NO_REGION_ID, name: 'Unaligned', bonus: 0, territoryIds: loose }]
      : regions
  }, [regions, territories])

  // Scroll the expanded region into the vertical centre of the parent
  // ScrollView (smooth). Fires when a region expands or the selected territory
  // changes — e.g. tapping a territory on the map expands its region and lands
  // here. We measure the region's plain container View (not the territory
  // TouchableOpacity, whose ref lacks measureLayout on react-native-web). Use
  // the Fabric native inner ref rather than getInnerViewNode's legacy numeric
  // handle; the latter is not a valid relative node under the current runtime.
  // Deferred a frame so the just-expanded body has laid out; scrollTo clamps,
  // so a short list just opens without moving (the "existing behavior for short
  // lists" case).
  const expandedRegionRef = React.useRef<View | null>(null)
  React.useEffect(() => {
    if (!expandedRegionId) return
    const scroll = scrollParentRef?.current
    if (!scroll) return
    const raf = requestAnimationFrame(() => {
      const node = expandedRegionRef.current
      const contentNode = (
        scroll as unknown as {
          getInnerViewRef?: () => React.ElementRef<typeof View> | null
        }
      ).getInnerViewRef?.()
      if (!node || !contentNode || typeof node.measureLayout !== 'function') return
      node.measureLayout(
        contentNode,
        (_x, y, _w, h) => {
          scroll.scrollTo({ y: centerScrollOffset(y, h, getViewportHeight?.() ?? 0), animated: true })
        },
        () => {},
      )
    })
    return () => cancelAnimationFrame(raf)
    // Fire ONLY on selection/expansion change — NOT on scrollParentRef (a stable
    // ref) or getViewportHeight (often an inline arrow = new identity each
    // render). Including them made this re-run every render and re-centre the
    // expanded region, which fought/overrode the map-Reset scroll-to-top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRegionId, selectedTerritoryId])

  return (
    // No wrapping card/title — the region rows fill the available width and use
    // the space. Small edge padding comes from the parent (infoPanel).
    <View className="gap-2">
      {groups.map((r) => {
        const color = regionColors[r.id] ?? '#8a8578'
        const isExpanded = expandedRegionId === r.id
        const isNoRegion = r.id === NO_REGION_ID
        return (
          <View
            key={r.id}
            // Ref only on the expanded region — the scroll-to-centre effect
            // measures this plain View against the parent ScrollView content.
            ref={isExpanded ? expandedRegionRef : undefined}
            className="rounded-md overflow-hidden"
            style={{
              borderColor: color,
              // Selected (expanded) region: thicker border + a glow in its own
              // colour so it clearly reads as selected in the list.
              borderWidth: isExpanded ? 3 : 2,
              ...(isExpanded
                ? {
                    shadowColor: color,
                    shadowOpacity: 0.7,
                    shadowRadius: 8,
                    shadowOffset: { width: 0, height: 0 },
                    elevation: 6,
                  }
                : {}),
            }}
          >
            <TouchableOpacity
              onPress={() => onToggleRegion(r.id)}
              accessibilityLabel={`${isExpanded ? 'Collapse' : 'Expand'} ${r.name} region`}
              accessibilityRole="button"
              accessibilityState={{ expanded: isExpanded }}
              className={`flex-row items-center gap-3 px-2 py-1.5 ${
                isExpanded ? 'bg-paper-up' : 'bg-paper'
              }`}
            >
              <View style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 2 }} />
              <Text className="font-stencil text-ink text-sm uppercase flex-1">{r.name}</Text>
              <Text className="font-typed text-ink-soft text-[10px]">
                {r.territoryIds.length} terr
              </Text>
              {/* No bonus chip for the synthetic group: "+0" would read as a
                  region worth nothing rather than "not a region". */}
              {isNoRegion ? (
                <Text className="font-typed text-ink-soft text-[10px]">no bonus</Text>
              ) : (
                <Text className="font-typed text-blood text-sm">+{r.bonus}</Text>
              )}
              <Text className="font-typed text-ink-soft text-xs w-3 text-center">
                {isExpanded ? '▾' : '▸'}
              </Text>
            </TouchableOpacity>

            {isExpanded && (
              <View
                className="px-2 py-2 gap-1"
                style={{ borderTopColor: color, borderTopWidth: 1 }}
              >
                {r.territoryIds
                  .map((tid) => territoryById[tid])
                  .filter((t): t is AccordionTerritory => !!t)
                  .map((t) => {
                    const isSelected = selectedTerritoryId === t.id
                    // Annotate a border with the die THIS territory rolls
                    // across it, when the map has raised it above the default
                    // d6 — otherwise a tower's d8 is invisible until you see an
                    // 8 come up mid-attack and wonder why.
                    const nameOf = (id: string) => territoryById[id]?.name ?? id
                    // "A8" / "D7" only for whichever mode the map raised above
                    // the default d6 — a bare number means both.
                    const diceTag = (
                      spec: number | { attack?: number; defend?: number } | undefined,
                    ) => {
                      if (typeof spec === 'number') return spec !== 6 ? ` (d${spec})` : ''
                      const bits: string[] = []
                      if (spec?.attack && spec.attack !== 6) bits.push(`A${spec.attack}`)
                      if (spec?.defend && spec.defend !== 6) bits.push(`D${spec.defend}`)
                      return bits.length ? ` (${bits.join('/')})` : ''
                    }
                    const withDice = (id: string) => `${nameOf(id)}${diceTag(t.borderDice?.[id])}`
                    // Incoming: the die is on the ATTACKER's record, not ours.
                    const withTheirDice = (id: string) =>
                      `${nameOf(id)}${diceTag(territoryById[id]?.borderDice?.[t.id])}`
                    const adjNames = isSelected ? t.adjacencies.map(withDice).sort() : []
                    const attacks = isSelected ? (t.oneWayBorders ?? []).map(withDice).sort() : []
                    const shelledBy = isSelected ? (attackedBy[t.id] ?? []).map(withTheirDice).sort() : []
                    const oneWayCount = (t.oneWayBorders ?? []).length
                    const customDice = Object.values(t.borderDice ?? {}).filter(
                      (v) => (typeof v === 'number' ? v !== 6 : !!(v.attack ?? v.defend)),
                    )
                    return (
                      <TouchableOpacity
                        key={t.id}
                        onPress={() => onSelectTerritory(t.id)}
                        accessibilityLabel={`${isSelected ? 'Deselect' : 'Select'} ${t.name}`}
                        accessibilityRole="button"
                        className={`rounded px-2 py-1 ${
                          isSelected ? 'bg-blood/15 border border-blood' : 'bg-paper-up'
                        }`}
                      >
                        <View className="flex-row items-center justify-between">
                          <Text className="font-typed text-ink text-sm">{t.name}</Text>
                          <Text className="font-typed text-ink-soft text-[10px]">
                            {t.adjacencies.length} adj
                            {oneWayCount > 0 ? ` · ${oneWayCount} 1-way` : ''}
                            {customDice.length > 0 ? ' · dice' : ''}
                          </Text>
                        </View>
                        {isSelected && (
                          <>
                            <Text className="font-typed text-ink-soft text-[11px] leading-4 mt-1">
                              Adjacent: {adjNames.join(' · ') || '// none'}
                            </Text>
                            {/* One-way reach, both directions. Labelled with the
                                direction rather than a bare list — "attacks" and
                                "attacked by" are not interchangeable, and the
                                incoming case is the one a player can't work out
                                from this territory's own record. */}
                            {attacks.length > 0 && (
                              <Text className="font-typed text-blood text-[11px] leading-4 mt-1">
                                Borders (1-way out): {attacks.join(' · ')}
                              </Text>
                            )}
                            {shelledBy.length > 0 && (
                              <Text className="font-typed text-blood text-[11px] leading-4 mt-1">
                                Attackable from: {shelledBy.join(' · ')}
                              </Text>
                            )}
                          </>
                        )}
                      </TouchableOpacity>
                    )
                  })}
              </View>
            )}
          </View>
        )
      })}
    </View>
  )
}
